import { DatePipe } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { isEqual } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, Subscriber } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  TrialInsightsPaymentMilestoneResponseType,
  TrialInsightsState,
} from '@models/trial-insights/trial-insights-store.model';
import { TrialInsightsQuery } from '@models/trial-insights/trial-insights.query';

import { listPaymentMilestoneSummaryQuery, SortOrder } from '@shared/services/gql.service';
import { GenericOperator } from '@shared/services/operator.service';
import { Utils } from '@shared/utils/utils';

import {
  PMChartType,
  TrialInsightsPaymentMilestoneChartService,
} from './payment-milestones-chart.service';
import { TrialInsightsPaymentMilestonesTableService } from './payment-milestones-table.service';
import {
  GenericTrialInsightsQuery,
  SortOrderDefault,
} from '../../classes/trial-insights-query.class';
import {
  TrialInsightsPaymentMilestoneKey,
  TrialInsightsTableRowData,
} from '../../models/trial-insights-table.model';

type Results = [
  State['data'],
  State['totalMilestones'],
  State['chartData'],
  TrialInsightsPaymentMilestoneKey,
];
type State = Partial<TrialInsightsState['paymentMilestone']>;

const sortDefaults: SortOrderDefault[] = [
  {
    buttonKey: TrialInsightsPaymentMilestoneKey.MILESTONES,
    defaultOrder: SortOrder.DESC,
  },
];

@Injectable({
  providedIn: 'root',
})
export class TrialInsightsPaymentMilestoneQueryService extends GenericTrialInsightsQuery<PMChartType> {
  chartService: TrialInsightsPaymentMilestoneChartService;
  tableService: TrialInsightsPaymentMilestonesTableService;
  trialInsightsQuery: TrialInsightsQuery;

  constructor() {
    const trialInsightsQuery = inject(TrialInsightsQuery);
    const chartService = inject(TrialInsightsPaymentMilestoneChartService);
    const tableService = inject(TrialInsightsPaymentMilestonesTableService);

    super({
      chartService,
      slice: 'paymentMilestone',
      sortDefaults,
      tableService,
      trialInsightsQuery,
    });

    this.trialInsightsQuery = trialInsightsQuery;
    this.chartService = chartService;
    this.tableService = tableService;
  }

  processPaymentMilestones$(): Observable<Results> {
    return combineLatest([
      this.data as Observable<TrialInsightsPaymentMilestoneResponseType[]>,
      this.trialInsightsQuery.select((state) => state.paymentMilestone.totalMilestones),
      this.trialInsightsQuery.select((state) => state.paymentMilestone.chartData),
      this.selectedKey as BehaviorSubject<TrialInsightsPaymentMilestoneKey>,
    ]).pipe(distinctUntilChanged(isEqual), this.processResponseData());
  }

  processResponseData() {
    const processFn = (sourceValue: Results, subscriber: Subscriber<Results>) => {
      const [data, totalMilestones, chartData, selectedKey] = sourceValue;

      this.totalAmount.next(Utils.currencyFormatter(totalMilestones || 0));

      this.renderTable(data || [], selectedKey);
      this.renderChart(chartData || []);

      subscriber.next(sourceValue);
    };

    const operatorConfig = new GenericOperator(processFn);

    return operatorConfig.operator();
  }

  private renderChart(data: listPaymentMilestoneSummaryQuery[]): void {
    const payments: number[] = [];
    const labels: string[] = [];

    data.forEach((paymentMilestone) => {
      labels.push(paymentMilestone.time_period);
      payments.push(paymentMilestone.total_amount);
    });

    const datasets = this.chartService.createDatasets(payments);
    const chartOptions = this.chartService.createChart(datasets, labels);

    this.chartOptions.next(chartOptions);
  }

  private renderTable(
    data: listPaymentMilestoneSummaryQuery[],
    selectedKey: TrialInsightsPaymentMilestoneKey
  ): void {
    const tableData: TrialInsightsTableRowData[] = (data || []).map((item) => {
      return {
        buttonKey: TrialInsightsPaymentMilestoneKey.MILESTONES,
        leftHeader: item.milestone_name,
        leftSubheader: item.vendor_name,
        rightHeader: Utils.currencyFormatter(item.total_amount),
        rightSubheader: new DatePipe('en-US').transform(item.time_period, 'MMMM d, yyyy') || '',
      };
    });

    const tableOptions = this.tableService.createTable(selectedKey, tableData);

    this.tableOptions.next(tableOptions);
  }
}
